import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const EMPTY = {
  name: "", designation: "", department: "", mobile: "", whatsapp: "", email: "",
  preferred: "both", whatsapp_enabled: true, email_enabled: true,
  receive_invoice: true, receive_lr: true, receive_pod: true, receive_delivery: true, is_active: true,
};

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [c, ct] = await Promise.all([api.get(`/clients/${id}`), api.get(`/contacts?client_id=${id}`)]);
    setClient(c.data); setContacts(ct.data);
  };
  useEffect(() => { load(); }, [id]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", designation: c.designation || "", department: c.department || "",
      mobile: c.mobile || "", whatsapp: c.whatsapp || "", email: c.email || "",
      preferred: c.preferred || "both",
      whatsapp_enabled: c.whatsapp_enabled !== false,
      email_enabled: c.email_enabled !== false,
      receive_invoice: c.receive_invoice !== false, receive_lr: c.receive_lr !== false,
      receive_pod: c.receive_pod !== false, receive_delivery: c.receive_delivery !== false,
      is_active: c.is_active !== false,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/contacts/${editing.id}`, form);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", { ...form, client_id: id });
        toast.success("Contact added");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete contact?")) return;
    await api.delete(`/contacts/${cid}`); load();
  };

  if (!client) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="page-client-detail" className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono">{client.code}</div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">{client.name}</h1>
        <div className="text-sm text-slate-500 mt-1">
          {client.legal_name} · {client.gst_number || "GST N/A"} · {client.city}, {client.state}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold">Client Contacts</h2>
        <button data-testid="btn-new-contact" onClick={openNew} className="bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-md flex items-center gap-2">
          <Plus size={16} /> Add Contact
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
              <th className="px-5 py-3">Name</th><th className="px-5 py-3">Department</th>
              <th className="px-5 py-3">WhatsApp</th><th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Preferred</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} data-testid={`contact-row-${c.id}`} className="table-row border-t border-slate-100">
                <td className="px-5 py-3 font-medium">{c.name}<div className="text-xs text-slate-500">{c.designation}</div></td>
                <td className="px-5 py-3 text-slate-600">{c.department}</td>
                <td className="px-5 py-3 font-mono text-xs">{c.whatsapp || "-"}</td>
                <td className="px-5 py-3 text-slate-600">{c.email || "-"}</td>
                <td className="px-5 py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100">{c.preferred}</span></td>
                <td className="px-5 py-3 text-right space-x-3">
                  <button data-testid={`btn-edit-contact-${c.id}`} onClick={() => openEdit(c)} className="text-xs text-slate-700 hover:text-slate-900 font-semibold inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                  <button onClick={() => remove(c.id)} className="text-rose-600 hover:text-rose-800"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {contacts.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-500">No contacts yet — add one to start sending notifications.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-40 p-6" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[85vh] overflow-y-auto" data-testid="dialog-contact">
            <h2 className="font-heading text-xl font-bold mb-4">{editing ? `Edit ${editing.name}` : "New Contact"}</h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["name", "Contact Person*", true], ["designation", "Designation"], ["department", "Department"],
                ["mobile", "Mobile"], ["whatsapp", "WhatsApp (with country code)"], ["email", "Email"],
              ].map(([k, l, req]) => (
                <div key={k}>
                  <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">{l}</label>
                  <input data-testid={`contact-${k}`} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </div>
              ))}
              <div>
                <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Preferred</label>
                <select value={form.preferred} onChange={(e) => setForm({ ...form, preferred: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
                  <option value="both">Both</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option>
                </select>
              </div>
              <div className="col-span-2 flex flex-wrap gap-4 pt-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.whatsapp_enabled} onChange={(e) => setForm({ ...form, whatsapp_enabled: e.target.checked })} /> WhatsApp on</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.email_enabled} onChange={(e) => setForm({ ...form, email_enabled: e.target.checked })} /> Email on</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button data-testid="btn-save-contact" className="px-4 py-2 text-sm bg-[var(--navy)] text-white rounded-md">{editing ? "Save" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
